import { useState } from "react";
import { Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import {
  RefreshCw,
  Database,
  HardDrive,
  AlertTriangle,
  Clock,
  ArrowRightLeft,
} from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useBackupClusters, useBackupOperations, useTriggerBackup } from "@/hooks/use-backup";
import { useIsPlatformAdmin } from "@/lib/auth";
import { ProdToDevDialog } from "@/components/ProdToDevDialog";
import { RestoreDialog } from "@/components/RestoreDialog";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, SortableHeader } from "@/components/shared/data-table";
import { OperationStatusBadge, OperationTypeBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { Button, Card, CardContent, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton } from "@fox1ck-org/ui";
import type { CNPGCluster } from "@/api/backup";

function formatTime(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60_000) return "только что";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} мин назад`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} ч назад`;
  return d.toLocaleDateString();
}

export default function Backups() {
  const isPlatformAdmin = useIsPlatformAdmin();
  const { data: clustersData, isLoading: clustersLoading, refetch: refetchClusters } = useBackupClusters();
  const [opFilter, setOpFilter] = useState<{ type?: string; status?: string }>({});
  const { data: opsData, isLoading: opsLoading } = useBackupOperations(opFilter);
  const triggerBackup = useTriggerBackup();
  const [showProdToDev, setShowProdToDev] = useState(false);
  const [showRestore, setShowRestore] = useState(false);

  const clusters = clustersData?.clusters ?? [];
  const operations = opsData?.items ?? [];
  const activeOps = operations.filter((o) => o.status === "running" || o.status === "pending");
  const failedOps = operations.filter((o) => o.status === "failed");

  const handleBackupNow = (cluster: CNPGCluster) => {
    if (triggerBackup.isPending) return;
    triggerBackup.mutate(
      { ns: cluster.namespace, name: cluster.name },
      {
        onSuccess: () => toast.success(`Резервная копия ${cluster.name} запущена`),
        onError: (err) => toast.error(`Копия не создана: ${err.message}`),
      }
    );
  };

  const clusterColumns: ColumnDef<CNPGCluster>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Название</SortableHeader>,
      cell: ({ row }) => (
        <Link to={`/backups/clusters/${row.original.namespace}/${row.original.name}`} className="font-medium text-foreground hover:text-primary">
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: "namespace",
      header: "Пространство имён",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.namespace}</span>,
    },
    {
      accessorKey: "instances",
      header: "Инстансы",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.readyInstances}/{row.original.instances}</span>,
    },
    {
      accessorKey: "phase",
      header: "Фаза",
      cell: ({ row }) => (
        <span className={row.original.phase.includes("healthy") ? "text-green-400" : "text-yellow-400"}>
          {row.original.phase}
        </span>
      ),
    },
    {
      accessorKey: "storageSize",
      header: "Хранилище",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.storageSize ?? "-"}</span>,
    },
    {
      accessorKey: "walArchivingStatus",
      header: "WAL",
      cell: ({ row }) => (
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${
            row.original.walArchivingStatus === "ok" ? "bg-green-400" :
            row.original.walArchivingStatus === "failed" ? "bg-red-400" : "bg-muted-foreground"
          }`}
          title={`WAL: ${row.original.walArchivingStatus ?? "неизвестно"}`}
        />
      ),
    },
    {
      accessorKey: "lastBackupTime",
      header: "Последняя копия",
      cell: ({ row }) => <span className="text-muted-foreground">{formatTime(row.original.lastBackupTime)}</span>,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button size="sm" variant="outline" onClick={() => handleBackupNow(row.original)} disabled={triggerBackup.isPending}>
          Создать копию
        </Button>
      ),
    },
  ];

  const operationColumns: ColumnDef<(typeof operations)[number]>[] = [
    {
      accessorKey: "operationType",
      header: "Тип",
      cell: ({ row }) => (
        <Link to={`/backups/operations/${row.original.id}`}>
          <OperationTypeBadge type={row.original.operationType} />
        </Link>
      ),
    },
    {
      accessorKey: "targetCluster",
      header: "Цель",
      cell: ({ row }) => (
        <Link to={`/backups/operations/${row.original.id}`} className="text-foreground hover:text-primary text-sm">
          {row.original.targetCluster}
          <span className="text-muted-foreground ml-1">({row.original.targetNamespace})</span>
        </Link>
      ),
    },
    {
      accessorKey: "status",
      header: "Статус",
      cell: ({ row }) => <OperationStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "createdBy",
      header: "Пользователь",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.createdBy}</span>,
    },
    {
      accessorKey: "createdAt",
      header: "Создан",
      cell: ({ row }) => <span className="text-muted-foreground">{formatTime(row.original.createdAt)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Резервные копии" description="Резервные копии баз, восстановление и перенос данных">
        {isPlatformAdmin && (
          <>
            <Button variant="outline" size="sm" onClick={() => setShowRestore(true)}>
              <Clock className="h-4 w-4" /> Восстановление
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowProdToDev(true)}>
              <ArrowRightLeft className="h-4 w-4" /> Из прода в дев
            </Button>
          </>
        )}
        <Button variant="ghost" size="icon" onClick={() => refetchClusters()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </PageHeader>

      {/* Summary Cards */}
      {clustersLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-4 w-16 mb-2" /><Skeleton className="h-8 w-12" /></CardContent></Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          <SummaryCard icon={<Database className="h-5 w-5 text-blue-400" />} label="Кластеры PG" value={clusters.length} />
          <SummaryCard icon={<HardDrive className="h-5 w-5 text-green-400" />} label="С копиями" value={clusters.filter((c) => c.hasBackupConfig).length} />
          <SummaryCard icon={<RefreshCw className="h-5 w-5 text-yellow-400" />} label="Активные операции" value={activeOps.length} />
          <SummaryCard icon={<AlertTriangle className="h-5 w-5 text-red-400" />} label="Сбой" value={failedOps.length} />
        </div>
      )}

      {/* Clusters */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-3">Кластеры PostgreSQL</h2>
        <DataTable
          columns={clusterColumns}
          data={clusters}
          isLoading={clustersLoading}
          showPagination={false}
          emptyState={<EmptyState icon={<Database className="h-10 w-10" />} title="Кластеров PostgreSQL нет" />}
        />
      </div>

      {/* Operations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Последние операции</h2>
          <div className="flex items-center gap-2">
            <Select value={opFilter.type ?? "all"} onValueChange={(v) => setOpFilter((f) => ({ ...f, type: v === "all" ? undefined : v }))}>
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <SelectValue placeholder="Все типы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все типы</SelectItem>
                <SelectItem value="backup">Резервная копия</SelectItem>
                <SelectItem value="restore">Восстановление</SelectItem>
                <SelectItem value="prod_to_dev">Из прода в дев</SelectItem>
              </SelectContent>
            </Select>
            <Select value={opFilter.status ?? "all"} onValueChange={(v) => setOpFilter((f) => ({ ...f, status: v === "all" ? undefined : v }))}>
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="pending">Ожидание</SelectItem>
                <SelectItem value="running">Работает</SelectItem>
                <SelectItem value="completed">Завершено</SelectItem>
                <SelectItem value="failed">Сбой</SelectItem>
                <SelectItem value="cancelled">Отменено</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DataTable
          columns={operationColumns}
          data={operations}
          isLoading={opsLoading}
          emptyState={<EmptyState title="Операций нет" />}
        />
      </div>

      {showProdToDev && <ProdToDevDialog clusters={clusters} onClose={() => setShowProdToDev(false)} />}
      {showRestore && <RestoreDialog clusters={clusters} onClose={() => setShowRestore(false)} />}
    </div>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        {icon}
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-bold text-foreground">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
